/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'reactstrap';
import { Button, Tag } from '@jem-open/jem-ui';

import moment from 'moment';

import axios from 'axios';
import { environment } from '../../../environment/environment';
import { DataTable, sortableHeader } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';
import AdvancePaymentFileUploadModal from './AdvancePaymentFileUploadModal';

const PAGE_SIZE = 10;

// Open a server-provided file URL, but only if it resolves to http(s) — guards
// against non-HTTP schemes (e.g. javascript:) sneaking through the value.
const openFileUrl = (url) => {
  const rawUrl = `${url || ''}`.trim();
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
    window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
  } catch {
    // Ignore invalid URLs.
  }
};

// Resolve a row's sort value via accessor, normalising empties to null.
const getSortValue = (accessor) => {
  const get =
    typeof accessor === 'function' ? accessor : (item) => item[accessor];
  return (item) => {
    const value = get(item);
    return value === null || value === undefined || value === '' ? null : value;
  };
};

// Compare two already-resolved values: empties last, numbers numerically,
// everything else as case-insensitive strings.
const compareValues = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const aNum = typeof a === 'number' ? a : Number(a);
  const bNum = typeof b === 'number' ? b : Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return aNum - bNum;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
};

const compareBy = (accessor, direction) => {
  const getValue = getSortValue(accessor);
  return (a, b) => {
    const result = compareValues(getValue(a), getValue(b));
    // Keep empties at the bottom regardless of sort direction.
    if (getValue(a) === null || getValue(b) === null) return result;
    return direction === 'desc' ? -result : result;
  };
};

const STATUS_TAG_VARIANT = {
  completed: 'success',
  complete: 'success',
  success: 'success',
  successful: 'success',
  processed: 'success',
  processing: 'processing',
  pending: 'pending',
  failed: 'failed',
  error: 'failed',
};

/**
 * @summary AdvancePaymentFiles component
 *
 * @param {Props} props
 * @component
 */
const AdvancePaymentFiles = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [advancePayments, setAdvancePayments] = useState([]);

  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const getAdvancePayments = useCallback((pageNumber) => {
    setIsLoading(true);
    axios
      .get(
        `${environment.baseUrl}/advancepaymentfileprocessingjob?pagination=true&page_size=${PAGE_SIZE}&page=${pageNumber}`,
      )
      .then((res) => {
        setAdvancePayments(res.data.results || res.data);
        setNumberOfPages(res.data.pages || 1);
        setCurrentPage(pageNumber);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const INITIAL_PAGE_NUMBER = 1;
    getAdvancePayments(INITIAL_PAGE_NUMBER);
  }, [getAdvancePayments]);

  const [sortConfig, setSortConfig] = useState({
    accessor: null,
    direction: 'asc',
  });

  const handleSort = useCallback((accessor) => {
    setSortConfig((prev) =>
      prev.accessor === accessor
        ? { accessor, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { accessor, direction: 'asc' },
    );
  }, []);

  const sortedPayments = useMemo(() => {
    const list = Array.isArray(advancePayments) ? advancePayments : [];
    if (!sortConfig.accessor) return list;
    return [...list].sort(
      compareBy(sortConfig.accessor, sortConfig.direction),
    );
  }, [advancePayments, sortConfig]);

  // Header label with an active-sort direction indicator.
  const headerLabel = (text, accessor) => (
    <span className="inline-flex items-center gap-xxs">
      {text}
      {sortConfig.accessor === accessor && (
        <span aria-hidden="true">
          {sortConfig.direction === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </span>
  );

  const columns = [
    {
      key: 'id',
      label: sortableHeader(headerLabel('ID', 'id'), 'id', handleSort),
      width: '80px',
    },
    {
      key: 'advance_payment_file',
      label: sortableHeader(
        headerLabel('File Name', 'advance_payment_file'),
        'advance_payment_file',
        handleSort,
      ),
      className: 'max-w-[260px]',
      render: (value) => (
        <div className="w-full truncate" title={value}>
          {value}
        </div>
      ),
    },
    {
      key: 'uploaded_by',
      label: sortableHeader(
        headerLabel('Uploaded By', 'uploaded_by'),
        'uploaded_by',
        handleSort,
      ),
    },
    {
      key: 'date_created',
      label: sortableHeader(
        headerLabel('Date Created', 'date_created'),
        'date_created',
        handleSort,
      ),
      width: '210px',
      render: (value) => moment(value).format('MMMM Do YYYY, h:mm:ss a'),
    },
    {
      key: 'status',
      label: sortableHeader(headerLabel('Status', 'status'), 'status', handleSort),
      render: (value) => (
        <Tag variant={STATUS_TAG_VARIANT[String(value).toLowerCase()] || 'neutral'}>
          {value}
        </Tag>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '160px',
      render: (value, advancePayment) => (
        <Button
          size="sm"
          variant="primary"
          onClick={() => openFileUrl(advancePayment.advance_payment_file)}>
          Download CSV
        </Button>
      ),
    },
  ];

  const emptyState = (
    <EmptyState
      icon="fas fa-file-invoice-dollar"
      message="No advance payment files found"
    />
  );

  return (
    <div className="min-h-screen bg-white p-md">
      <div
        className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
        <div>
          <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
            Advance Payment Files
          </h1>
          <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
            Upload and review processed advance payment files.
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowUploadModal(true)}>
          Upload Advance Payment File
        </Button>
      </div>

      <Card className="shadow border-0">
        <div className="table-responsive">
          <DataTable
            columns={columns}
            data={sortedPayments}
            loading={isLoading}
            emptyState={emptyState}
            skeletonRows={8}
          />
        </div>
      </Card>

      {!isLoading && numberOfPages > 1 && (
        <div className="mt-md flex justify-center">
          <Pager
            currentPage={currentPage}
            totalPages={numberOfPages}
            onPageChange={getAdvancePayments}
          />
        </div>
      )}

      {showUploadModal && (
        <AdvancePaymentFileUploadModal
          showAdvancePaymentFileUploadModalModal={setShowUploadModal}
          onUploaded={() => getAdvancePayments(1)}
        />
      )}
    </div>
  );
};

export default AdvancePaymentFiles;
